import { caminhoDoQr, gerarQr } from "./qr";
import type { NivelDeCorrecao } from "./qr";

/**
 * QR Code em SVG — **sem biblioteca e sem CDN**.
 *
 * Mesma decisão do {@link CodigoDeBarras}: numa cadeia de suprimentos de
 * software de sistema regulado, uma dependência a menos é uma superfície a
 * menos. O codificador vive em `qr.ts`, ao lado, e é conferido por teste.
 *
 * <h2>Por que ele é `<svg>` e não uma imagem</h2>
 *
 * Porque ele vai para o **papel**. Navegador não imprime `background` sem
 * "Gráficos de plano de fundo" ligado, e o padrão do Chrome é DESLIGADO — este
 * projeto já perdeu a palavra QUARENTENA de uma folha por causa disso. Conteúdo
 * SVG imprime sempre.
 *
 * <h2>A zona de silêncio mora dentro do `viewBox`</h2>
 *
 * Não é margem CSS. Assim ela acompanha o símbolo em qualquer escala e ninguém
 * a perde ajustando um `padding` — e QR sem zona de silêncio é QR que o leitor
 * não encontra, mesmo impresso perfeito.
 *
 * <h2>🔴 `nivel` NÃO TEM PADRÃO, e a ausência é a decisão</h2>
 *
 * Ele tinha: `M`, justificado assim — *"`M` é o padrão porque a URL do MVP é
 * longa: subir para `Q` empurra o símbolo para uma versão maior, e um QR maior
 * numa etiqueta de tamanho fixo significa módulo menor"*. **A razão foi
 * revogada.** Aquela URL não existe mais: o que vai no papel hoje é
 * `/qr/<32 hexadecimais>` — 70 bytes no domínio da demonstração, ADR-0006, a
 * emenda de 2026-08-14 —, e o único chamador do sistema, a etiqueta de
 * quarentena, pede `nivel="Q"` **explicitamente**, remedido em **0,898 mm** por
 * módulo na folha A4, quase o dobro do piso prático de 0,5 mm.
 *
 * O artefato está certo. O **padrão** é que virou armadilha: um valor herdado
 * por omissão, sustentado por uma frase sobre um sistema que não existe mais.
 * O próximo chamador que escrevesse `<CodigoQr valor={…} rotulo={…} />`
 * receberia `M` sem ter decidido nada — e ninguém confere nível de correção
 * olhando um QR. Descobre-se com o leitor na mão, diante do tambor.
 *
 * Por isso ele é **obrigatório**. A escolha entre correção e tamanho de módulo
 * depende do papel, da impressora e da sujeira do lugar em que o adesivo vai
 * morar; nada disso este componente sabe, e nada disso se assume por ausência
 * de informação — o mesmo princípio com que a `Tabela` recusa virar cartão sem
 * `matchMedia`. Aqui não há sequer um caso geral seguro para eleger.
 *
 * ⚠️ O que trava isso é `CodigoQr.teste.tsx`, com um `@ts-expect-error` sobre
 * uma chamada sem `nivel`: se o padrão voltar, a chamada volta a compilar, o
 * `@ts-expect-error` fica sem erro para esperar e o `npm run typecheck`
 * reprova. Um teste de execução não pegaria isto — a armadilha é de compilação.
 */
export function CodigoQr({
  valor,
  nivel,
  silencio = 4,
  rotulo,
  className,
}: {
  /** O que o QR carrega. Aqui, a URL pública composta a partir de configuração. */
  valor: string;
  /**
   * L 7% · M 15% · Q 25% · H 30% de recuperação. **Sem padrão** — ver acima.
   *
   * Mais correção significa mais módulos no mesmo espaço de papel, e módulo
   * menor é o que de fato derruba a leitura numa térmica de 203 dpi. Correção
   * alta com módulo abaixo do piso do leitor protege contra sujeira um código
   * que já não se lê limpo. A conta é de quem chama, porque só quem chama sabe
   * em que tamanho o símbolo vai sair impresso.
   */
  nivel: NivelDeCorrecao;
  /** Módulos claros ao redor. Quatro é o mínimo da norma. */
  silencio?: number;
  /** Nome acessível. Sem ele o QR é desenho mudo para quem lê a tela. */
  rotulo: string;
  className?: string;
}) {
  const qr = gerarQr(valor, nivel);
  const lado = qr.modulos + silencio * 2;

  return (
    <svg
      className={className}
      viewBox={`0 0 ${lado} ${lado}`}
      /* Sem isto o rasterizador antialiasa a borda de cada módulo, e um leitor
         de galpão lê a média do pixel — não o módulo. */
      shapeRendering="crispEdges"
      role="img"
      aria-label={rotulo}
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* O branco é DESENHADO, não herdado do papel: o QR precisa do próprio
          fundo claro mesmo que alguém o coloque sobre uma superfície escura. */}
      <rect width={lado} height={lado} fill="#fff" />
      <path d={caminhoDoQr(qr, silencio)} fill="#000" />
    </svg>
  );
}
